import React from 'react';
import { DownloadButton } from '../../../ui-kit';
import { downloadReportExport } from '../../../services/manualDashboardService';
import type { DateRange } from '../../../ui-kit';

/**
 * Excel downloads for a report's tables and case lists, for the filters and
 * months on screen. `button(block)` is the same Download control every board
 * table has; `problem(block)` is what went wrong with that one, for its footer.
 */
export function useReportDownload(dashboardId: string | undefined, filters: Record<string, string[]>, range: DateRange) {
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState<{ block: string; message: string } | null>(null);

  const run = async (block: string, grain?: string, dateBasis?: string) => {
    if (!dashboardId) return;
    setDownloading(block);
    setFailed(null);
    try {
      await downloadReportExport(dashboardId, block, { filters, start: range.start, end: range.end, grain, dateBasis });
    } catch (err) {
      setFailed({ block, message: `Could not download: ${(err as Error).message}` });
    } finally {
      setDownloading(null);
    }
  };

  /** `grain` and `dateBasis`: how the panel is shown, so the file matches it. */
  const button = (block: string, opts: { description?: string; tooltip?: string; disabled?: boolean; grain?: string; dateBasis?: string } = {}) => (
    <DownloadButton
      formats={[{ key: 'xlsx', label: 'Excel (.xlsx)', description: opts.description, onDownload: () => void run(block, opts.grain, opts.dateBasis) }]}
      tooltip={opts.tooltip ?? 'Download this table as Excel, as shown'}
      loading={downloading === block}
      disabled={opts.disabled}
    />
  );

  const problem = (block: string) => (failed?.block === block ? failed.message : null);

  return { button, problem };
}

export type ReportDownload = ReturnType<typeof useReportDownload>;
